<template>
  <v-card
    class="dialog-card"
    style="overflow: visible">
    <!--Overflow visible so that you can see the full asset drop down-->
    <v-dialog
      max-width="600px"
      v-model="sceneSelectorDialog">
      <ScenePicker
        v-if="sceneSelectorDialog"
        :exclude_channel="channel_did"
        v-on:selected="sceneSelected"
        v-on:close="sceneSelectorDialog = false" />
    </v-dialog>

    <ModalHeader
      icon="mdi-share-all"
      modalTitle="Add Trigger"
      @close="emit('close')" />

    <div
      class="mx-3 modal-body"
      style="
        flex-direction: column;
        margin-right: 0px !important;
        margin-left: 0px !important;
        padding-right: 12px !important;
        padding-left: 12px !important;
      ">
      <v-select
        item-title="name"
        return-object
        :hide-selected="false"
        :items="filteredTriggers"
        filled
        persistent-hint
        hint="Trigger Type"
        @update:modelValue="triggerSelected"></v-select>
      <div
        v-if="newTrigger.type"
        class="mt-3"
        style="overflow-y: auto">
        <!-- Handle scene_select specially for ScenePicker integration -->
        <div
          v-for="(svar, index) in newTrigger.vars"
          :key="index">
          <div
            class="mb-2"
            v-if="svar.type === 'scene_select'"
            style="display: flex">
            <v-btn
              v-if="
                !newTrigger.vars[index]['value'] &&
                newTrigger.vars[index]['type'] === 'scene_select'
              "
              color="textBoxBackground"
              @click="sceneSelectorDialog = true"
              variant="tonal"
              small
              block>
              Select Scene
            </v-btn>
            <v-chip
              v-else
              closable
              @click:close="newTrigger.vars[index]['value'] = undefined"
              color="primary"
              class="ma-2">
              {{ newTrigger.vars[index]['value']?.name || 'Scene Selected' }}
            </v-chip>
          </div>
          <!-- Use TriggerVars for other types -->
          <TriggerVars
            v-else
            :vars="[svar]" />
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <v-spacer />
      <v-btn
        @click="addTrigger()"
        :disabled="!isValidTrigger"
        variant="flat"
        block
        color="primary">
        Add
      </v-btn>
    </div>
  </v-card>
</template>
<script setup>
import ScenePicker from './ScenePicker.vue';
import { ref, computed, onMounted } from 'vue';
import { $http } from '../../stores/http.js';
import { deluge } from '../../stores/deluge.js';
import ModalHeader from './Modals/ModalHeader.vue';
import TriggerVars from './TriggerVars.vue';

// Props:
// - item: The scene/queue/template object to add the trigger to
// - context: 'scene' | 'queue' | 'template' | 'button' (determines which triggers are available)
// - channel_did: Optional, used for scene picker exclusion
const props = defineProps(['item', 'context', 'channel_did']);
const emit = defineEmits(['triggerupdated', 'close']);

const sceneSelectorDialog = ref(false);

// All available trigger types with context-based filtering
// rejects: array of contexts where this trigger should NOT appear
const allTriggers = ref([
  {
    name: 'Preset',
    type: 'start_preset',
    rejects: [], // Available in all contexts
    vars: [
      {
        name: 'Preset',
        value: { name: '', value: '' },
        type: 'preset_select',
        hint: 'Starts a specific preset, returns to original after playback',
      },
      {
        hint: '',
        name: 'Frames',
        options: 'frames',
        type: 'frames',
        value: [],
      },
    ],
  },
  {
    name: 'Asset',
    type: 'start_asset',
    rejects: [], // Available in all contexts
    vars: [
      {
        name: 'Asset',
        value: { name: '', value: '' },
        type: 'asset_select',
        hint: 'Starts a specific Asset, returns to original after playback',
      },
      {
        hint: '',
        name: 'Frames',
        options: 'frames',
        type: 'frames',
        value: [],
      },
    ],
  },
  {
    name: 'Scene',
    type: 'start_scene',
    rejects: [], // Available in all contexts
    vars: [
      {
        name: 'Scene',
        value: undefined,
        type: 'scene_select',
        hint: 'Starts a specific scene, returns to original after playback',
      },
      {
        name: 'Channels',
        value: [],
        type: 'autocomplete',
        options: 'channels',
        hint: '',
      },
    ],
  },
  {
    name: 'Name Match',
    type: 'start_name_match',
    rejects: [], // Available in all contexts
    vars: [
      {
        name: 'Match Name',
        value: undefined,
        type: 'text',
        hint: 'Trigger name matches for scenes and queues',
      },
      {
        name: 'Channel Allowlist',
        value: [],
        type: 'autocomplete',
        options: 'channels',
        hint: 'If empty all channels will be evaluated',
      },
    ],
  },
  {
    name: 'HTTP Request',
    type: 'http_req',
    rejects: [], // Available in all contexts
    vars: [
      {
        name: 'Method',
        value: undefined,
        type: 'enum',
        options: ['GET', 'POST', 'PUT', 'DELETE'],
        hint: '',
      },
      {
        name: 'URL',
        value: undefined,
        type: 'text',
        hint: '',
      },
      {
        name: 'Body',
        value: undefined,
        type: 'text',
        hint: '',
      },
      {
        name: 'Username',
        value: undefined,
        type: 'text',
        hint: '',
      },
      {
        name: 'Password',
        value: undefined,
        type: 'text',
        hint: '',
      },
    ],
  },
  {
    name: 'Scene Data',
    type: 'start_scene_data',
    rejects: ['scene', 'queue', 'template'], // Only available for buttons
    vars: [
      {
        name: 'SceneData',
        value: undefined,
        type: 'scene_data_all',
        hint: 'Select scene data to trigger',
      },
    ],
  },
  {
    name: 'Serial Command',
    type: 'start_serial_command',
    rejects: [], // Available in all contexts
    vars: [
      {
        name: 'Serial Port',
        value: '',
        type: 'text',
        hint: 'E.g COM7 or /dev/ttyUSB0',
      },
      {
        name: 'Write Command',
        value: '',
        type: 'text',
        hint: 'E.g ASCII command: b"?<CR><LF>"',
      },
    ],
  },
  {
    name: 'OSC Send',
    type: 'osc_send',
    rejects: [], // Available in all contexts
    vars: [
      {
        name: 'IP',
        value: '127.0.0.1',
        type: 'text',
        hint: 'Target IP address',
      },
      {
        name: 'Port',
        value: '9000',
        type: 'text',
        hint: 'Target port',
      },
      {
        name: 'Path',
        value: '/',
        type: 'text',
        hint: 'OSC path (e.g. /scene/play)',
      },
      {
        name: 'Data',
        value: '',
        type: 'text',
        hint: 'Data to send (optional)',
      },
    ],
  },
]);

// Filter triggers based on context
const filteredTriggers = computed(() => {
  const context = props.context || 'button'; // Default to button (most permissive)
  return allTriggers.value.filter(
    (trigger) => !trigger.rejects || !trigger.rejects.includes(context)
  );
});

const newTrigger = ref({
  name: '',
  type: '',
  vars: [],
});

// Validation: check if trigger is properly configured
const isValidTrigger = computed(() => {
  if (!newTrigger.value.type || newTrigger.value.vars.length === 0) {
    return false;
  }
  // Check first var has a value (primary input)
  const firstVar = newTrigger.value.vars[0];
  if (firstVar.value === undefined || firstVar.value === '') {
    // For object values (like scene_select, asset_select), check if value property exists
    if (typeof firstVar.value === 'object' && firstVar.value !== null) {
      return firstVar.value.value !== undefined && firstVar.value.value !== '';
    }
    return false;
  }
  return true;
});

function sceneSelected(scene) {
  sceneSelectorDialog.value = false;
  newTrigger.value.vars[0]['value'] = { name: scene.name, value: scene.id };
}

function triggerSelected(obj) {
  newTrigger.value = JSON.parse(JSON.stringify(obj));
}

function addTrigger() {
  // If item prop is provided, add directly to item.triggers
  if (props.item && props.item.triggers) {
    props.item.triggers.push(newTrigger.value);
    emit('close');
  } else {
    // Otherwise emit the trigger for parent to handle
    emit('triggerupdated', newTrigger.value);
    emit('close');
  }
}
</script>
